import { FC, ReactNode } from 'react';

// MUI
import {
  ToggleButtonGroup,
  ToggleButton,
  ToggleButtonGroupProps,
  FormControl,
  FormLabel,
  FormHelperText
} from '@mui/material';

// Third party
import { Controller, useFormContext } from 'react-hook-form';

type ToggleValue = string | number;

type TRHFToggleOption<T extends ToggleValue = ToggleValue> = {
  label: ReactNode;
  value: T;
  disabled?: boolean;
};

type TRHFToggleButtonGroup<T extends ToggleValue = ToggleValue> =
  Omit<ToggleButtonGroupProps, 'value' | 'onChange' | 'children'> & {
  name: string;
  options: TRHFToggleOption<T>[];
  formLabel?: ReactNode;
  helperText?: ReactNode;
};

const RHFToggleButtonGroup: FC<TRHFToggleButtonGroup> = ({
                                                           name,
                                                           options,
                                                           formLabel,
                                                           helperText,
                                                           exclusive = true,
                                                           sx,
                                                           ...other
                                                         }) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl error={!!error} sx={{ width: '100%', ...sx }}>
          {formLabel && <FormLabel sx={{ mb: 1, color: "#FFF" }}>{formLabel}</FormLabel>}

          <ToggleButtonGroup
            {...other}
            exclusive={exclusive}
            value={field.value ?? null}
            onChange={(_, val) => {
              if (val !== null) field.onChange(val);
            }}
            size={'small'}
            sx={(theme) => ({
              width: '100%',
              display: 'flex',
              // base button look
              '& .MuiToggleButton-root': {
                flex: '1 1 0',
                minWidth: 0,
                color: '#B5B7BA',
                borderColor: '#51585E',
                backgroundColor: 'transparent',
                transition: theme.transitions.create(
                  ['background-color', 'border-color', 'box-shadow', 'color'],
                  { duration: theme.transitions.duration.shortest }
                ),
                '&:not(.Mui-selected):hover': {
                  backgroundColor: '#111923',
                  borderColor: '#5B636A',
                },
                '&:active': {
                  backgroundColor: '#0D141C',
                },
                '&.Mui-focusVisible': {
                  outline: '2px solid #1DB0BF',
                  outlineOffset: 2,
                },
              },
              '& .MuiToggleButton-root.Mui-selected': {
                backgroundColor: '#454C53',
                color: '#FFFFFF',
                borderColor: '#616971',
                '&:hover': {
                  backgroundColor: '#40474F',
                  borderColor: '#6B737B',
                },
                '&:active': {
                  backgroundColor: '#3A4148',
                },
              },
            })}
          >
            {options.map((opt) => (
              <ToggleButton key={String(opt.value)} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {(error || helperText) && (
            <FormHelperText>{error?.message ?? helperText}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
};

export { RHFToggleButtonGroup };
